/** 
 * Expression Parser & Evaluator (Ticket T-04)
 * 
 * Implements complete expression grammar with:
 * - Variable assignments and references
 * - Fraction literals and arithmetic  
 * - Operator precedence: ^ > * / > + -
 * - Right-associative exponentiation
 * - Cycle detection for variable dependencies
 * - Comprehensive error handling
 */

import { Fraction, add, sub, mul, div, pow, parseFraction, toNumber } from './fractions.js';

// =============================================================================
// TOKEN TYPES & TOKENIZER
// =============================================================================

export enum TokenType {
  NUMBER = 'NUMBER',
  FRACTION = 'FRACTION', 
  IDENT = 'IDENT',
  ASSIGN = 'ASSIGN',
  PLUS = 'PLUS',
  MINUS = 'MINUS',
  MULTIPLY = 'MULTIPLY',
  DIVIDE = 'DIVIDE',
  POWER = 'POWER',
  LPAREN = 'LPAREN',
  RPAREN = 'RPAREN',
  EOF = 'EOF',
  UNARY_MINUS = 'UNARY_MINUS'
}

export interface Token {
  type: TokenType;
  value: string;
  position: number;
}

/**
 * Tokenizes expression source code, handling unary minus transformation and implicit multiplication
 */
export function tokenize(src: string): Token[] {
  const tokens: Token[] = [];
  let pos = 0;

  const isAlpha = (c: string) => /[a-zA-Z]/.test(c);
  const isDigit = (c: string) => /[0-9]/.test(c);
  const isAlphaNum = (c: string) => isAlpha(c) || isDigit(c);

  while (pos < src.length) {
    const char = src[pos];

    // Skip whitespace
    if (/\s/.test(char)) {
      pos++;
      continue;
    }

    // Numbers (including decimals)
    if (isDigit(char) || (char === '.' && pos + 1 < src.length && isDigit(src[pos + 1]))) {
      let value = '';
      while (pos < src.length && (isDigit(src[pos]) || src[pos] === '.')) {
        value += src[pos];
        pos++;
      }
      
      // Check if this is a fraction (number followed by /)
      if (pos < src.length && src[pos] === '/') {
        pos++; // consume '/'
        let denominator = '';
        while (pos < src.length && isDigit(src[pos])) {
          denominator += src[pos];
          pos++;
        }
        if (denominator === '') {
          throw new Error(`Invalid fraction: missing denominator at position ${pos}`);
        }
        tokens.push({ type: TokenType.FRACTION, value: value + '/' + denominator, position: pos - value.length - 1 - denominator.length });
      } else {
        tokens.push({ type: TokenType.NUMBER, value, position: pos - value.length });
      }
      continue;
    }

    // Identifiers (variables a-z)
    if (isAlpha(char)) {
      let value = '';
      while (pos < src.length && isAlphaNum(src[pos])) {
        value += src[pos];
        pos++;
      }
      tokens.push({ type: TokenType.IDENT, value, position: pos - value.length });
      continue;
    }

    // Operators and symbols
    switch (char) {
      case '=':
        tokens.push({ type: TokenType.ASSIGN, value: char, position: pos });
        break;
      case '+':
        tokens.push({ type: TokenType.PLUS, value: char, position: pos });
        break;
      case '-':
        // Handle unary minus: transform to UNARY_MINUS when after start, (, or operator
        const prevToken = tokens[tokens.length - 1];
        const isUnary = !prevToken || 
          prevToken.type === TokenType.LPAREN ||
          prevToken.type === TokenType.ASSIGN ||
          prevToken.type === TokenType.PLUS ||
          prevToken.type === TokenType.MINUS ||
          prevToken.type === TokenType.MULTIPLY ||
          prevToken.type === TokenType.DIVIDE ||
          prevToken.type === TokenType.POWER ||
          prevToken.type === TokenType.UNARY_MINUS;
        
        tokens.push({ 
          type: isUnary ? TokenType.UNARY_MINUS : TokenType.MINUS, 
          value: char, 
          position: pos 
        });
        break;
      case '*':
        tokens.push({ type: TokenType.MULTIPLY, value: char, position: pos });
        break;
      case '/':
        tokens.push({ type: TokenType.DIVIDE, value: char, position: pos });
        break;
      case '^':
        tokens.push({ type: TokenType.POWER, value: char, position: pos });
        break;
      case '(':
        tokens.push({ type: TokenType.LPAREN, value: char, position: pos });
        break;
      case ')':
        tokens.push({ type: TokenType.RPAREN, value: char, position: pos });
        break;
      default:
        throw new Error(`Unexpected character '${char}' at position ${pos}`);
    }
    pos++;
  }

  // Post-process tokens to add implicit multiplication
  const processedTokens: Token[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const currentToken = tokens[i];
    const nextToken = tokens[i + 1];
    
    processedTokens.push(currentToken);
    
    // Add implicit multiplication between:
    // 1. Number followed by variable (e.g., 2a -> 2*a)
    // 2. Variable followed by number (e.g., a2 -> a*2) 
    // 3. Variable followed by variable (e.g., ab -> a*b)
    // 4. Number followed by left parenthesis (e.g., 2(3) -> 2*(3))
    // 5. Variable followed by left parenthesis (e.g., a(3) -> a*(3))
    // 6. Right parenthesis followed by number (e.g., (2)3 -> (2)*3)
    // 7. Right parenthesis followed by variable (e.g., (2)a -> (2)*a)
    // 8. Right parenthesis followed by left parenthesis (e.g., (2)(3) -> (2)*(3))
    
    if (nextToken && (
      // Number followed by variable or left parenthesis
      (currentToken.type === TokenType.NUMBER && (nextToken.type === TokenType.IDENT || nextToken.type === TokenType.LPAREN)) ||
      // Variable followed by number, variable, or left parenthesis  
      (currentToken.type === TokenType.IDENT && (nextToken.type === TokenType.NUMBER || nextToken.type === TokenType.IDENT || nextToken.type === TokenType.LPAREN)) ||
      // Right parenthesis followed by number, variable, or left parenthesis
      (currentToken.type === TokenType.RPAREN && (nextToken.type === TokenType.NUMBER || nextToken.type === TokenType.IDENT || nextToken.type === TokenType.LPAREN))
    )) {
      processedTokens.push({ 
        type: TokenType.MULTIPLY, 
        value: '*', 
        position: currentToken.position + currentToken.value.length 
      });
    }
  }

  processedTokens.push({ type: TokenType.EOF, value: '', position: pos });
  return processedTokens;
}

// =============================================================================
// AST NODE TYPES
// =============================================================================

export type AstNode = 
  | NumberNode 
  | FractionNode 
  | VariableNode 
  | AssignmentNode 
  | BinaryOpNode
  | UnaryOpNode;

export interface NumberNode {
  type: 'Number';
  value: number;
}

export interface FractionNode {
  type: 'Fraction';
  value: Fraction;
}

export interface VariableNode {
  type: 'Variable';
  name: string;
}

export interface AssignmentNode {
  type: 'Assignment';
  variable: string;
  expression: AstNode;
}

export interface BinaryOpNode {
  type: 'BinaryOp';
  operator: '+' | '-' | '*' | '/' | '^';
  left: AstNode;
  right: AstNode;
}

export interface UnaryOpNode {
  type: 'UnaryOp';
  operator: '-';
  operand: AstNode;
}

// =============================================================================
// RECURSIVE DESCENT PARSER
// =============================================================================

export class Parser {
  private tokens: Token[];
  private current = 0;

  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  /**
   * Parse a complete statement (assignment or expression)
   */
  parse(): AstNode {
    const result = this.statement();
    if (!this.isAtEnd()) {
      throw new Error(`Unexpected token '${this.peek().value}' at position ${this.peek().position}`);
    }
    return result;
  }

  private statement(): AstNode {
    // Check for assignment: IDENT = expression
    if (this.check(TokenType.IDENT) && this.checkNext(TokenType.ASSIGN)) {
      const variable = this.advance().value;
      this.advance(); // consume '='
      const expression = this.expression();
      return { type: 'Assignment', variable, expression };
    }
    
    return this.expression();
  }

  private expression(): AstNode {
    return this.additive();
  }

  private additive(): AstNode {
    let expr = this.multiplicative();

    while (this.match(TokenType.PLUS, TokenType.MINUS)) {
      const operator = this.previous().value as '+' | '-';
      const right = this.multiplicative();
      expr = { type: 'BinaryOp', operator, left: expr, right };
    }

    return expr;
  }

  private multiplicative(): AstNode {
    let expr = this.power();

    while (this.match(TokenType.MULTIPLY, TokenType.DIVIDE)) {
      const operator = this.previous().value as '*' | '/';
      const right = this.power();
      expr = { type: 'BinaryOp', operator, left: expr, right };
    }

    return expr;
  }

  private power(): AstNode {
    let expr = this.unary();

    // Right-associative: 2^3^2 = 2^(3^2)
    if (this.match(TokenType.POWER)) {
      const right = this.power(); // Recursive call for right-associativity
      expr = { type: 'BinaryOp', operator: '^', left: expr, right };
    }

    return expr;
  }

  private unary(): AstNode {
    if (this.match(TokenType.UNARY_MINUS)) {
      const operand = this.unary();
      return { type: 'UnaryOp', operator: '-', operand };
    }

    return this.primary();
  }

  private primary(): AstNode {
    if (this.match(TokenType.NUMBER)) {
      const value = parseFloat(this.previous().value);
      return { type: 'Number', value };
    }

    if (this.match(TokenType.FRACTION)) {
      const fractionStr = this.previous().value;
      try {
        const fraction = parseFraction(fractionStr);
        return { type: 'Fraction', value: fraction };
      } catch (error) {
        throw new Error(`Invalid fraction '${fractionStr}' at position ${this.previous().position}`);
      }
    }

    if (this.match(TokenType.IDENT)) {
      const name = this.previous().value;
      return { type: 'Variable', name };
    }

    if (this.match(TokenType.LPAREN)) {
      const expr = this.expression();
      if (!this.match(TokenType.RPAREN)) {
        throw new Error(`Expected ')' at position ${this.peek().position}`);
      }
      return expr;
    }

    throw new Error(`Unexpected token '${this.peek().value}' at position ${this.peek().position}`);
  }

  // Parser utility methods
  private match(...types: TokenType[]): boolean {
    for (const type of types) {
      if (this.check(type)) {
        this.advance();
        return true;
      }
    }
    return false;
  }

  private check(type: TokenType): boolean {
    if (this.isAtEnd()) return false;
    return this.peek().type === type;
  }

  private checkNext(type: TokenType): boolean {
    if (this.current + 1 >= this.tokens.length) return false;
    return this.tokens[this.current + 1].type === type;
  }

  private advance(): Token {
    if (!this.isAtEnd()) this.current++;
    return this.previous();
  }

  private isAtEnd(): boolean {
    return this.peek().type === TokenType.EOF;
  }

  private peek(): Token {
    return this.tokens[this.current];
  }

  private previous(): Token {
    return this.tokens[this.current - 1];
  }
}

// =============================================================================
// EVALUATION ENVIRONMENT & CYCLE DETECTION
// =============================================================================

export interface Environment {
  [variable: string]: Fraction;
}

export interface EvaluationResult {
  value?: Fraction;
  error?: string;
}

/**
 * Evaluate AST node in given environment with cycle detection
 */
export function evaluateAst(
  ast: AstNode, 
  env: Environment, 
  evaluationStack: string[] = []
): EvaluationResult {
  try {
    switch (ast.type) {
      case 'Number':
        // Convert number to fraction
        return { value: parseFraction(ast.value.toString()) };

      case 'Fraction':
        return { value: ast.value };

      case 'Variable': {
        // Cycle detection
        if (evaluationStack.includes(ast.name)) {
          const cycle = [...evaluationStack, ast.name].slice(evaluationStack.indexOf(ast.name));
          return { error: `Circular dependency detected: ${cycle.join(' → ')}` };
        }

        if (!(ast.name in env)) {
          return { error: `Undefined variable: ${ast.name}` };
        }

        return { value: env[ast.name] };
      }

      case 'Assignment': {
        const valueResult = evaluateAst(ast.expression, env, [...evaluationStack, ast.variable]);
        if (valueResult.error) {
          return valueResult;
        }
        // Assignment returns the assigned value for chaining
        return { value: valueResult.value! };
      }

      case 'UnaryOp': {
        const operandResult = evaluateAst(ast.operand, env, evaluationStack);
        if (operandResult.error) return operandResult;

        if (ast.operator === '-') {
          return { value: { n: -operandResult.value!.n, d: operandResult.value!.d } };
        }
        
        return { error: `Unknown unary operator: ${ast.operator}` };
      }

      case 'BinaryOp': {
        const leftResult = evaluateAst(ast.left, env, evaluationStack);
        if (leftResult.error) return leftResult;

        const rightResult = evaluateAst(ast.right, env, evaluationStack);
        if (rightResult.error) return rightResult;

        const left = leftResult.value!;
        const right = rightResult.value!;

        switch (ast.operator) {
          case '+':
            return { value: add(left, right) };
          case '-':
            return { value: sub(left, right) };
          case '*':
            return { value: mul(left, right) };
          case '/':
            if (right.n === 0) {
              return { error: 'Division by zero' };
            }
            return { value: div(left, right) };
          case '^':
            // Power requires integer exponent
            if (right.d !== 1) {
              return { error: 'Exponent must be an integer' };
            }
            try {
              return { value: pow(left, right) };
            } catch (error) {
              return { error: (error as Error).message };
            }
          default:
            return { error: `Unknown operator: ${ast.operator}` };
        }
      }

      default:
        return { error: `Unknown AST node type` };
    }
  } catch (error) {
    return { error: (error as Error).message };
  }
}

// =============================================================================
// HIGH-LEVEL API
// =============================================================================

/**
 * Parse expression source code into AST
 */
export function parse(src: string): AstNode {
  const tokens = tokenize(src);
  const parser = new Parser(tokens);
  return parser.parse();
}

/**
 * Evaluate expression source in environment
 */
export function evaluate(src: string, env: Environment = {}): EvaluationResult {
  try {
    const ast = parse(src);
    return evaluateAst(ast, env);
  } catch (error) {
    return { error: (error as Error).message };
  }
}

/**
 * Evaluate program (multiple lines) with variable assignments
 * Returns results for each line and final environment
 */
export function evaluateProgram(lines: string[], initialEnv: Environment = {}): {
  results: (EvaluationResult)[];
  env: Environment;
} {
  const env = { ...initialEnv };
  const results: EvaluationResult[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      results.push({ value: parseFraction('0') }); // Empty line
      continue;
    }

    try {
      const ast = parse(trimmed);
      const result = evaluateAst(ast, env);
      
      // If it's an assignment, update environment
      if (ast.type === 'Assignment' && result.value) {
        env[ast.variable] = result.value;
      }
      
      results.push(result);
    } catch (error) {
      results.push({ error: (error as Error).message });
    }
  }

  return { results, env };
}

/**
 * Get all variables referenced in an expression (for dependency tracking)
 */
export function getVariableReferences(ast: AstNode): string[] {
  const vars: string[] = [];
  
  function visit(node: AstNode) {
    switch (node.type) {
      case 'Variable':
        if (!vars.includes(node.name)) {
          vars.push(node.name);
        }
        break;
      case 'Assignment':
        visit(node.expression);
        break;
      case 'BinaryOp':
        visit(node.left);
        visit(node.right);
        break;
      case 'UnaryOp':
        visit(node.operand);
        break;
    }
  }
  
  visit(ast);
  return vars;
}